/**
 * ProductGridCard — gold-standard product card used in:
 *  - /soluciones/{slug}    (via SolutionPage)
 *  - /hardware/{slug}      (via HardwareCategoryPage)
 *
 * Pure presentational: takes a product object + addItem callback (cart context).
 * Optional compare functionality if `compareList` + `toggleCompare` are passed.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, FileDown } from 'lucide-react';
import { formatCLP, isPlaceholderPrice } from '@/lib/format';

const TIER_CONFIG = {
  esencial: { label: 'Esencial', subtitle: 'Implementación ágil', dots: 1, color: '#D3DD18', bg: 'bg-[#D3DD18]/10 text-[#8B9A00]' },
  entry:    { label: 'Esencial', subtitle: 'Implementación ágil', dots: 1, color: '#D3DD18', bg: 'bg-[#D3DD18]/10 text-[#8B9A00]' },
  pro:      { label: 'Pro',      subtitle: 'La más elegida',      dots: 3, color: '#0191B4', bg: 'bg-[#0191B4]/10 text-[#0191B4]' },
  mid:      { label: 'Pro',      subtitle: 'La más elegida',      dots: 3, color: '#0191B4', bg: 'bg-[#0191B4]/10 text-[#0191B4]' },
  premium:  { label: 'Premium',  subtitle: 'Máximo desempeño',    dots: 5, color: '#FE7A15', bg: 'bg-[#FE7A15]/10 text-[#FE7A15]' },
};

const ProductGridCard = ({ product: p, addItem, compareList, toggleCompare }) => {
  const tier = TIER_CONFIG[p.tier] || TIER_CONFIG.pro;
  // Detail URL uses the primary `category`. Fallback to first hw_slug.
  const detailCat = p.category || (Array.isArray(p.hw_slugs) && p.hw_slugs[0]) || 'hardware';
  const detailHref = `/hardware/${detailCat}/${p.slug}`;
  const showCompare = typeof toggleCompare === 'function' && Array.isArray(compareList);
  const isChecked = showCompare ? !!compareList.find(x => x.slug === p.slug) : false;
  const isFull = showCompare ? (compareList.length >= 4 && !isChecked) : false;

  return (
    <div className="flex flex-col h-full p-4 rounded-xl bg-white border border-slate-200 shadow-sm text-center group" data-testid={`product-card-${p.slug}`}>
      {/* Category badge */}
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
        {p.category_display || (p.category ? p.category.replace(/-/g, ' ').toUpperCase() : 'HARDWARE')}
      </span>

      {/* Product image */}
      <div className="w-full aspect-square rounded-lg bg-slate-50 my-3 overflow-hidden flex items-center justify-center">
        {p.image ? (
          <img src={p.image} alt={`${p.brand || ''} ${p.name} - ALLCOM Chile`} className="w-full h-full object-contain p-2" loading="lazy" />
        ) : (
          <span className="text-3xl font-bold text-slate-200" style={{ fontFamily: 'Syne' }}>{(p.brand || 'A').charAt(0)}</span>
        )}
      </div>

      {/* Tier indicator */}
      <div className="flex items-center justify-center gap-2 mb-2">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map(d => (
            <div key={d} className="w-2 h-2 rounded-full" style={{ backgroundColor: d <= tier.dots ? tier.color : '#E2E8F0' }} />
          ))}
        </div>
        <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${tier.bg}`}>{tier.label}</span>
        <span className="text-[9px] text-slate-400">— {tier.subtitle}</span>
      </div>

      {/* Brand + Name */}
      <span className="text-[10px] text-slate-400 uppercase font-semibold">{p.brand}</span>
      <Link to={detailHref}>
        <h4 className="text-sm font-semibold text-slate-900 group-hover:text-[#0EA5E9] transition-colors">{p.name}</h4>
      </Link>
      {p.summary && <p className="text-[10px] text-slate-500 mt-1 leading-relaxed line-clamp-2">{p.summary}</p>}

      {/* Price */}
      {(p.price_display || p.price) && (
        <div className="mt-2">
          {isPlaceholderPrice(p.price_display || p.price) ? (
            <div>
              <span className="text-base font-bold text-[#0EA5E9]" style={{ fontFamily: 'Syne,sans-serif' }}>Cotizar</span>
              <span className="text-[9px] text-slate-400 ml-1">precio sujeto a stock</span>
            </div>
          ) : p.liquidacion ? (
            <div className="relative">
              {!isPlaceholderPrice(p.price_original) && (
                <span className="text-xs text-slate-400 line-through mr-2">{formatCLP(p.price_original) || p.price_original || ''}</span>
              )}
              <span className="text-base font-bold text-white px-2 py-0.5 bg-red-500 rounded-lg tabular-nums" style={{ fontFamily: 'Syne,sans-serif' }}>{formatCLP(p.price_display || p.price)}</span>
              <span className="text-[9px] text-red-500 font-semibold ml-1">LIQUIDACIÓN</span>
            </div>
          ) : (
            <div>
              <span className="text-base font-bold text-[#0A2540] tabular-nums" style={{ fontFamily: 'Syne,sans-serif' }}>{formatCLP(p.price_display || p.price)}</span>
              <span className="text-[9px] text-slate-400 ml-1">+ IVA</span>
            </div>
          )}
        </div>
      )}

      {/* Use cases */}
      {p.use_cases && p.use_cases.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center mt-2">
          {p.use_cases.map((uc, j) => (
            <span key={j} className="text-[9px] px-2 py-0.5 bg-slate-50 border border-slate-100 text-slate-500 rounded-full">{uc}</span>
          ))}
        </div>
      )}

      {/* PDFs */}
      {(p.spec_doc?.url || p.spec_pdf || p.brochure_doc?.url || p.brochure_pdf) && (
        <div className="flex gap-2 justify-center mt-2 flex-wrap">
          {(p.spec_doc?.url || p.spec_pdf) && (
            <a
              href={(p.spec_doc?.url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${p.spec_doc.url}?download=1` : (p.spec_doc?.url || p.spec_pdf))}
              target="_blank" rel="noopener noreferrer"
              className="text-[10px] px-2 py-0.5 rounded-md bg-sky-50 text-[#0EA5E9] hover:bg-[#0EA5E9] hover:text-white flex items-center gap-1 transition-colors"
              data-testid={`download-spec-${p.slug}`}
            >
              <FileDown className="w-3 h-3" />Ficha técnica
            </a>
          )}
          {(p.brochure_doc?.url || p.brochure_pdf) && (
            <a
              href={(p.brochure_doc?.url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${p.brochure_doc.url}?download=1` : (p.brochure_doc?.url || p.brochure_pdf))}
              target="_blank" rel="noopener noreferrer"
              className="text-[10px] px-2 py-0.5 rounded-md bg-slate-50 text-slate-600 hover:bg-slate-700 hover:text-white flex items-center gap-1 transition-colors"
              data-testid={`download-brochure-${p.slug}`}
            >
              <FileDown className="w-3 h-3" />Brochure
            </a>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-auto pt-3">
        <Link to={detailHref} className="flex-1 text-center py-2 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
          Ver detalle
        </Link>
        <button
          onClick={() => addItem && addItem(p)}
          className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 transition-colors"
          data-testid={`add-to-cart-${p.slug}`}
        >
          <ShoppingBag className="w-3 h-3" strokeWidth={1.8} /> Cotizar
        </button>
      </div>

      {/* Compare checkbox (optional) */}
      {showCompare && (
        <label
          className={`flex items-center gap-1.5 mt-2 text-[10px] cursor-pointer select-none ${isFull ? 'opacity-40 cursor-not-allowed' : 'text-slate-500 hover:text-[#0EA5E9]'}`}
          data-testid={`compare-toggle-${p.slug}`}
        >
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => !isFull && toggleCompare(p)}
            disabled={isFull}
            className="w-3 h-3 accent-[#0EA5E9]"
          />
          <span>Comparar {isFull ? '(máx. 4)' : ''}</span>
        </label>
      )}
    </div>
  );
};

export default ProductGridCard;
